import { createFileRoute } from '@tanstack/react-router';
import { use } from 'react';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderNextNavButton,
  HeaderNav,
  HeaderSidebarTrigger,
  HeaderPrevNavButton,
  useHeaderNav,
  HeaderMobileChatButton,
} from '@/app/ui/components/header';
import { EvaluationTypeLabelsEnum, ClauseScannerAnalysisType } from '@/app/utils/constants';
import { ContractsIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { AppContext } from '@/app/appContext';
import { useGetClauseScannerSubcategoryQuery } from '@/app/hooks/useClauseScanner';
import { ClausesDetailPageContent } from '@/app/components/features/evaluations/Clauses/ClausesDetailPageContent';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getContractDetailsQuery } from '@/app/hooks/queries/useContracts';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/contracts/$contractId/_layout/(main)/clauses/$subcategoryId/$clauseId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.contractHome');
  const tFeature = useTranslations('features.clauses');
  const { contractId, projectId, subcategoryId, clauseId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getContractDetailsQuery(contractId));
  const { userMeta } = use(AppContext);
  const organizationId = userMeta?.organizationId || '';

  const { data: subCategory } = useGetClauseScannerSubcategoryQuery({
    subCategoryId: subcategoryId,
    resourceId: contractId,
    type: ClauseScannerAnalysisType.CONTRACT,
    enabled: !!organizationId,
  });

  const allClauses = subCategory?.clauses || [];

  const { handlePrevious, handleNext, isPreviousDisabled, isNextDisabled, currentIndex } =
    useHeaderNav(allClauses, clauseId, (id) => ({
      to: '/project/$projectId/contracts/$contractId/clauses/$subcategoryId/$clauseId',
      params: { projectId, contractId, subcategoryId, clauseId: id },
    }));

  if (!subCategory) return null;

  return (
    <>
      <Header>
        <HeaderBackButton to="/project/$projectId/contracts/$contractId/clauses" />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <HugeiconsIcon icon={ContractsIcon} />
                {t('title')}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.CONTRACT} />
                {evaluation.name}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId/clauses"
                params={{ projectId, contractId }}
              >
                {tFeature('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId/clauses/$subcategoryId"
                params={{ projectId, contractId, subcategoryId }}
              >
                <span className="line-clamp-1">{subCategory.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbPage className="tabular-nums">#{currentIndex + 1}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderNav>
          <HeaderPrevNavButton onClick={handlePrevious} disabled={isPreviousDisabled} />
          <HeaderNextNavButton onClick={handleNext} disabled={isNextDisabled} />
        </HeaderNav>
        <HeaderSidechatButton />
        <HeaderMobileChatButton />
      </Header>
      <ClausesDetailPageContent
        evaluationId={contractId}
        type={ClauseScannerAnalysisType.CONTRACT}
        currentIndex={currentIndex}
      />
    </>
  );
}
